import { motion } from "framer-motion";
import {
  IconCode,
  IconBrandReact,
  IconBrandNodejs,
  IconDatabase,
  IconSpeakerphone,
  IconSearch,
  IconBrandGoogle,
  IconChartBar,
  IconPalette,
  IconLayout,
  IconPointer,
  IconBrandPython,
  IconServer,
  IconTerminal2,
  IconCalculator,
  IconReceipt,
  IconChartPie,
  IconDeviceDesktop,
  IconKeyboard,
  IconFileText,
  IconShieldLock,
  IconLock,
  IconBug,
  IconNetwork,
} from "@tabler/icons-react";

import CourseSection from "./CourseSection";
import { fadeUp, stagger, cardAnimation } from "./animations";

export function MernStackSection({ detail = false }) {
  return (
    <CourseSection
      route="/institute/mern-stack"
      detail={detail}
      number="01"
      label="Full Stack Development"
      title="MERN Stack Development"
      description="Learn to build modern full-stack web applications using MongoDB, Express.js, React.js and Node.js through practical application development."
    >
    
            <motion.div
              variants={stagger}
              initial="hidden"
              whileInView="visible"
              viewport={{
                once: true,
                amount: 0.2,
              }}
              className="
                grid
                gap-3
                sm:grid-cols-2
                lg:grid-cols-4
              "
            >
    
              <TechnologyBox
                icon={<IconBrandReact size={24} />}
                title="React"
                text="Interactive frontend applications"
              />
    
              <TechnologyBox
                icon={<IconBrandNodejs size={24} />}
                title="Node.js"
                text="Backend application development"
              />
    
              <TechnologyBox
                icon={<IconCode size={24} />}
                title="Express"
                text="API and server architecture"
              />
    
              <TechnologyBox
                icon={<IconDatabase size={24} />}
                title="MongoDB"
                text="Modern database development"
              />
    
            </motion.div>
    
    
            {/* DEVELOPMENT FLOW */}
    
            <motion.div
              variants={fadeUp}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              className="
                mt-6
                rounded-[18px]
                border
                border-neutral-200
                bg-[#101010]
                p-5
                text-white
                sm:p-7
              "
            >
    
              <p
                className="
                  mb-5
                  text-[10px]
                  font-bold
                  uppercase
                  tracking-[0.2em]
                  text-[#55aef3]
                "
              >
                Full Stack Workflow
              </p>
    
              <div
                className="
                  flex
                  flex-col
                  gap-3
                  md:flex-row
                  md:items-center
                "
              >
    
                <FlowStep text="UI" />
    
                <FlowArrow />
    
                <FlowStep text="React" />
    
                <FlowArrow />
    
                <FlowStep text="API" />
    
                <FlowArrow />
    
                <FlowStep text="Node" />
    
                <FlowArrow />
    
                <FlowStep text="MongoDB" />
    
              </div>
    
            </motion.div>
    
    </CourseSection>
  );
}

export function DigitalMarketingSection({ detail = false }) {
  return (
    <CourseSection
      route="/institute/digital-marketing"
      detail={detail}
      number="02"
      label="Digital Growth"
      title="Digital Marketing"
      description="Learn practical digital marketing through SEO, paid advertising, social media, content strategy, analytics and lead generation."
      gray
    >
    
            <motion.div
              variants={stagger}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              className="
                grid
                gap-4
                lg:grid-cols-[1.15fr_0.85fr]
              "
            >
    
              {/* CAMPAIGN PANEL */}
    
              <motion.div
                variants={cardAnimation}
                className="
                  rounded-[20px]
                  border
                  border-neutral-200
                  bg-white
                  p-6
                  sm:p-8
                "
              >
    
                <div
                  className="
                    flex
                    items-center
                    justify-between
                    border-b
                    border-neutral-100
                    pb-5
                  "
                >
    
                  <div>
    
                    <p className="text-xs text-neutral-400">
                      Campaign learning
                    </p>
    
                    <h3 className="mt-1 text-xl font-bold">
                      From search to conversion
                    </h3>
    
                  </div>
    
                  <IconChartBar
                    size={27}
                    className="text-[#55aef3]"
                  />
    
                </div>
    
    
                <div className="mt-7 space-y-5">
    
                  <MarketingProgress
                    name="SEO"
                    percentage="90%"
                    width="90%"
                  />
    
                  <MarketingProgress
                    name="Google Ads"
                    percentage="82%"
                    width="82%"
                  />
    
                  <MarketingProgress
                    name="Social Media"
                    percentage="86%"
                    width="86%"
                  />
    
                  <MarketingProgress
                    name="Analytics"
                    percentage="74%"
                    width="74%"
                  />
    
                </div>
    
              </motion.div>
    
    
              {/* TOOLS */}
    
              <motion.div
                variants={cardAnimation}
                className="grid gap-3 sm:grid-cols-2 lg:grid-cols-1"
              >
    
                <MiniSkill
                  icon={<IconSearch />}
                  title="SEO"
                  text="Search visibility & keyword strategy"
                />
    
                <MiniSkill
                  icon={<IconBrandGoogle />}
                  title="Google Ads"
                  text="Search and performance campaigns"
                />
    
                <MiniSkill
                  icon={<IconSpeakerphone />}
                  title="Social Media"
                  text="Content and audience growth"
                />
    
              </motion.div>
    
            </motion.div>
    
    </CourseSection>
  );
}

export function UiUxSection({ detail = false }) {
  return (
    <CourseSection
      route="/institute/ui-ux"
      detail={detail}
      number="03"
      label="Product Design"
      title="UI/UX Design"
      description="Learn to turn ideas into intuitive digital experiences using user research, wireframes, visual design, prototypes and usability principles."
    >
    
            <motion.div
              variants={fadeUp}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              className="
                relative
                rounded-[22px]
                border
                border-neutral-200
                bg-white
                p-4
                sm:p-7
              "
            >
    
              {/* DESIGN TOPBAR */}
    
              <div
                className="
                  flex
                  items-center
                  gap-2
                  border-b
                  border-neutral-200
                  pb-4
                "
              >
                <span className="h-2.5 w-2.5 rounded-full bg-neutral-300" />
                <span className="h-2.5 w-2.5 rounded-full bg-neutral-300" />
                <span className="h-2.5 w-2.5 rounded-full bg-neutral-300" />
    
                <span className="ml-3 text-xs text-neutral-400">
                  WE3X Design Workspace
                </span>
              </div>
    
    
              <div
                className="
                  mt-6
                  grid
                  gap-4
                  md:grid-cols-[0.7fr_1.3fr_0.8fr]
                "
              >
    
                <DesignPanel
                  icon={<IconPointer />}
                  title="Research"
                  text="Understand users and problems."
                />
    
                <div
                  className="
                    rounded-[16px]
                    bg-[#f6f6f6]
                    p-5
                  "
                >
    
                  <div className="h-5 w-1/3 rounded bg-neutral-200" />
    
                  <div className="mt-5 grid grid-cols-2 gap-3">
    
                    <div className="h-24 rounded-xl bg-white" />
    
                    <div className="h-24 rounded-xl bg-[#55aef3]/15" />
    
                  </div>
    
                  <div className="mt-3 h-16 rounded-xl bg-white" />
    
                  <div className="mt-4 flex gap-2">
    
                    <div className="h-8 w-24 rounded-lg bg-[#111]" />
    
                    <div className="h-8 w-20 rounded-lg border border-neutral-200 bg-white" />
    
                  </div>
    
                </div>
    
    
                <div className="grid gap-3">
    
                  <DesignPanel
                    icon={<IconLayout />}
                    title="Wireframe"
                    text="Plan structure and flows."
                  />
    
                  <DesignPanel
                    icon={<IconPalette />}
                    title="Visual"
                    text="Create polished interfaces."
                  />
    
                </div>
    
              </div>
    
            </motion.div>
    
    </CourseSection>
  );
}

export function PythonFullStackSection({ detail = false }) {
  return (
    <CourseSection
      route="/institute/python-full-stack"
      detail={detail}
      number="04"
      label="Software Development"
      title="Python Full Stack"
      description="Build complete web applications using Python, frontend technologies, databases, APIs and modern backend development concepts."
      gray
    >
    
            <motion.div
              variants={fadeUp}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              className="
                overflow-hidden
                rounded-[22px]
                bg-[#0d0d0d]
                text-white
              "
            >
    
              {/* TERMINAL HEADER */}
    
              <div
                className="
                  flex
                  items-center
                  justify-between
                  border-b
                  border-white/10
                  px-5
                  py-4
                "
              >
    
                <div className="flex gap-2">
    
                  <span className="h-2.5 w-2.5 rounded-full bg-white/20" />
                  <span className="h-2.5 w-2.5 rounded-full bg-white/20" />
                  <span className="h-2.5 w-2.5 rounded-full bg-white/20" />
    
                </div>
    
                <p className="text-xs text-white/40">
                  python-fullstack.py
                </p>
    
              </div>
    
    
              <div className="grid gap-8 p-6 md:grid-cols-2 sm:p-8">
    
                <div
                  className="
                    font-mono
                    text-sm
                    leading-8
                    text-white/70
                  "
                >
                  <p className="text-[#55aef3]">
                    class WE3XDeveloper:
                  </p>
    
                  <p className="pl-5">
                    frontend = "HTML / CSS / JS"
                  </p>
    
                  <p className="pl-5">
                    backend = "Python"
                  </p>
    
                  <p className="pl-5">
                    database = "SQL"
                  </p>
    
                  <p className="pl-5">
                    api = "REST"
                  </p>
    
                  <p className="mt-5 text-green-400">
                    # Build real applications
                  </p>
    
                </div>
    
    
                <div className="grid gap-3 sm:grid-cols-2">
    
                  <DarkSkill
                    icon={<IconBrandPython />}
                    title="Python"
                  />
    
                  <DarkSkill
                    icon={<IconServer />}
                    title="Backend"
                  />
    
                  <DarkSkill
                    icon={<IconDatabase />}
                    title="Database"
                  />
    
                  <DarkSkill
                    icon={<IconTerminal2 />}
                    title="Projects"
                  />
    
                </div>
    
              </div>
    
            </motion.div>
    
    </CourseSection>
  );
}

export function TallyPrimeSection({ detail = false }) {
  return (
    <CourseSection
      route="/institute/tally-prime"
      detail={detail}
      number="05"
      label="Accounting & Business"
      title="Tally Prime"
      description="Learn computerised accounting, business transactions, GST concepts, inventory handling and practical Tally Prime workflows."
    >
    
            <motion.div
              variants={fadeUp}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              className="
                rounded-[22px]
                border
                border-neutral-200
                bg-white
                p-5
                sm:p-7
              "
            >
    
              <div
                className="
                  grid
                  gap-4
                  md:grid-cols-3
                "
              >
    
                <FinanceMetric
                  icon={<IconCalculator />}
                  label="Accounting"
                  value="Ledgers"
                />
    
                <FinanceMetric
                  icon={<IconReceipt />}
                  label="Transactions"
                  value="Invoices"
                />
    
                <FinanceMetric
                  icon={<IconChartPie />}
                  label="Business"
                  value="Reports"
                />
    
              </div>
    
    
              <div
                className="
                  mt-5
                  overflow-hidden
                  rounded-[14px]
                  border
                  border-neutral-200
                "
              >
    
                <LedgerRow
                  title="Sales Entry"
                  value="Practical"
                />
    
                <LedgerRow
                  title="Purchase Entry"
                  value="Practical"
                />
    
                <LedgerRow
                  title="GST"
                  value="Included"
                />
    
                <LedgerRow
                  title="Inventory"
                  value="Included"
                  last
                />
    
              </div>
    
            </motion.div>
    
    </CourseSection>
  );
}

export function BasicComputersSection({ detail = false }) {
  return (
    <CourseSection
      route="/institute/basic-computers"
      detail={detail}
      number="06"
      label="Digital Foundations"
      title="Basic Computers"
      description="Build strong computer fundamentals and gain confidence using essential digital tools for education, office work and everyday productivity."
      gray
    >
    
            <motion.div
              variants={stagger}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              className="
                grid
                gap-4
                sm:grid-cols-2
                lg:grid-cols-4
              "
            >
    
              <DesktopModule
                icon={<IconDeviceDesktop />}
                title="Computer Basics"
              />
    
              <DesktopModule
                icon={<IconKeyboard />}
                title="Typing & Files"
              />
    
              <DesktopModule
                icon={<IconFileText />}
                title="Office Tools"
              />
    
              <DesktopModule
                icon={<IconSearch />}
                title="Internet Skills"
              />
    
            </motion.div>
    
    </CourseSection>
  );
}

export function CyberSecuritySection({ detail = false }) {
  return (
    <CourseSection
      route="/institute/cyber-security"
      detail={detail}
      number="07"
      label="Security & Networks"
      title="Cyber Security"
      description="Understand cybersecurity foundations, networks, threats, vulnerabilities and essential security practices through practical learning."
    >
    
            <motion.div
              variants={fadeUp}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              className="
                relative
                overflow-hidden
                rounded-[22px]
                bg-[#08131f]
                p-6
                text-white
                sm:p-8
              "
            >
    
              <div
                className="
                  pointer-events-none
                  absolute
                  right-0
                  top-0
                  h-72
                  w-72
                  bg-[#55aef3]/10
                  blur-[90px]
                "
              />
    
    
              <div className="relative">
    
                <div
                  className="
                    flex
                    items-center
                    justify-between
                    border-b
                    border-white/10
                    pb-5
                  "
                >
    
                  <div>
    
                    <p
                      className="
                        text-[10px]
                        font-bold
                        uppercase
                        tracking-[0.2em]
                        text-[#55aef3]
                      "
                    >
                      Security Lab
                    </p>
    
                    <h3 className="mt-2 text-xl font-bold">
                      Threat monitoring environment
                    </h3>
    
                  </div>
    
                  <div
                    className="
                      flex
                      h-11
                      w-11
                      items-center
                      justify-center
                      rounded-xl
                      bg-[#55aef3]
                    "
                  >
                    <IconShieldLock size={22} />
                  </div>
    
                </div>
    
    
                <div
                  className="
                    mt-6
                    grid
                    gap-3
                    sm:grid-cols-2
                    lg:grid-cols-4
                  "
                >
    
                  <SecurityModule
                    icon={<IconLock />}
                    title="Security Basics"
                  />
    
                  <SecurityModule
                    icon={<IconNetwork />}
                    title="Networking"
                  />
    
                  <SecurityModule
                    icon={<IconBug />}
                    title="Threats"
                  />
    
                  <SecurityModule
                    icon={<IconShieldLock />}
                    title="Protection"
                  />
    
                </div>
    
              </div>
    
            </motion.div>
    
    </CourseSection>
  );
}

function TechnologyBox({ icon, title, text }) {
  return (
    <motion.div
      variants={cardAnimation}
      whileHover={{ y: -5 }}
      className="
        rounded-[16px]
        border
        border-neutral-200
        bg-white
        p-5
      "
    >

      <div
        className="
          flex
          h-10
          w-10
          items-center
          justify-center
          rounded-xl
          bg-blue-50
          text-[#55aef3]
        "
      >
        {icon}
      </div>

      <h3 className="mt-5 text-sm font-bold">
        {title}
      </h3>

      <p className="mt-2 text-xs leading-5 text-neutral-500">
        {text}
      </p>

    </motion.div>
  );
}


function FlowStep({ text }) {
  return (
    <div
      className="
        flex-1
        rounded-xl
        border
        border-white/10
        bg-white/[0.05]
        px-4
        py-4
        text-center
        text-xs
        font-bold
      "
    >
      {text}
    </div>
  );
}


function FlowArrow() {
  return (
    <span className="hidden text-[#55aef3] md:block">
      →
    </span>
  );
}


/* =========================================================
   MARKETING
========================================================= */

function MarketingProgress({
  name,
  percentage,
  width,
}) {
  return (
    <div>

      <div className="flex justify-between text-xs font-semibold">

        <span>{name}</span>

        <span className="text-neutral-400">
          {percentage}
        </span>

      </div>

      <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-neutral-100">

        <motion.div
          initial={{ width: 0 }}
          whileInView={{ width }}
          viewport={{ once: true }}
          transition={{
            duration: 1,
            ease: [0.22, 1, 0.36, 1],
          }}
          className="h-full rounded-full bg-[#55aef3]"
        />

      </div>

    </div>
  );
}


function MiniSkill({ icon, title, text }) {
  return (
    <motion.div
      variants={cardAnimation}
      className="
        flex
        gap-4
        rounded-[16px]
        border
        border-neutral-200
        bg-white
        p-5
      "
    >

      <div
        className="
          flex
          h-10
          w-10
          shrink-0
          items-center
          justify-center
          rounded-xl
          bg-blue-50
          text-[#55aef3]
        "
      >
        {icon}
      </div>

      <div>

        <h3 className="text-sm font-bold">
          {title}
        </h3>

        <p className="mt-1 text-xs leading-5 text-neutral-500">
          {text}
        </p>

      </div>

    </motion.div>
  );
}


/* =========================================================
   UI UX
========================================================= */

function DesignPanel({ icon, title, text }) {
  return (
    <div
      className="
        rounded-[16px]
        border
        border-neutral-200
        bg-white
        p-5
      "
    >

      <div className="text-[#55aef3]">
        {icon}
      </div>

      <h3 className="mt-5 text-sm font-bold">
        {title}
      </h3>

      <p className="mt-2 text-xs leading-5 text-neutral-500">
        {text}
      </p>

    </div>
  );
}


/* =========================================================
   PYTHON
========================================================= */

function DarkSkill({ icon, title }) {
  return (
    <div
      className="
        rounded-xl
        border
        border-white/10
        bg-white/[0.05]
        p-4
      "
    >

      <div className="text-[#55aef3]">
        {icon}
      </div>

      <p className="mt-4 text-xs font-semibold">
        {title}
      </p>

    </div>
  );
}


/* =========================================================
   TALLY
========================================================= */

function FinanceMetric({ icon, label, value }) {
  return (
    <div className="rounded-[15px] bg-[#f7f7f7] p-5">

      <div className="flex items-center justify-between">

        <span className="text-[#55aef3]">
          {icon}
        </span>

        <span className="text-[10px] uppercase tracking-wider text-neutral-400">
          {label}
        </span>

      </div>

      <p className="mt-6 text-lg font-bold">
        {value}
      </p>

    </div>
  );
}


function LedgerRow({ title, value, last }) {
  return (
    <div
      className={`
        flex
        items-center
        justify-between
        px-5
        py-4
        text-sm
        ${!last ? "border-b border-neutral-200" : ""}
      `}
    >

      <span className="text-neutral-600">
        {title}
      </span>

      <span
        className="
          rounded-full
          bg-blue-50
          px-3
          py-1
          text-xs
          font-bold
          text-[#55aef3]
        "
      >
        {value}
      </span>

    </div>
  );
}


/* =========================================================
   BASIC COMPUTERS
========================================================= */

function DesktopModule({ icon, title }) {
  return (
    <motion.div
      variants={cardAnimation}
      whileHover={{
        y: -6,
      }}
      className="
        overflow-hidden
        rounded-[18px]
        border
        border-neutral-200
        bg-white
      "
    >

      <div
        className="
          flex
          items-center
          gap-1.5
          border-b
          border-neutral-200
          bg-[#fafafa]
          px-4
          py-3
        "
      >
        <span className="h-2 w-2 rounded-full bg-neutral-200" />
        <span className="h-2 w-2 rounded-full bg-neutral-200" />
        <span className="h-2 w-2 rounded-full bg-neutral-200" />
      </div>


      <div className="p-6">

        <div className="text-[#55aef3]">
          {icon}
        </div>

        <h3 className="mt-7 text-sm font-bold">
          {title}
        </h3>

        <div className="mt-4 h-1 w-12 rounded-full bg-[#55aef3]" />

      </div>

    </motion.div>
  );
}


/* =========================================================
   CYBER SECURITY
========================================================= */

function SecurityModule({ icon, title }) {
  return (
    <motion.div
      variants={cardAnimation}
      whileHover={{
        y: -5,
      }}
      className="
        rounded-[14px]
        border
        border-white/10
        bg-white/[0.04]
        p-5
      "
    >

      <div className="text-[#55aef3]">
        {icon}
      </div>

      <p className="mt-6 text-xs font-semibold text-white/80">
        {title}
      </p>

      <div className="mt-3 flex items-center gap-2">

        <span className="h-1.5 w-1.5 rounded-full bg-green-400" />

        <span className="text-[10px] text-white/35">
          Active
        </span>

      </div>

    </motion.div>
  );
}


